import { useContext, useState } from "react";
import { ToastContext } from "../../../../hooks/context";
import { useGenerateInvoice } from "../../../../hooks/api/mutations/useInventory";
import {
  useAcceptOrRejectOrdereMutation,
  usePrintReturnsReceipt,
  useReverseOrder,
} from "../../../../hooks/api/mutations/useOrder";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import {
  printOrderReceipt,
  printPaymentReceipt,
} from "../../../../services/sales-point";
import { usePrint } from "../../../../hooks/usePrint";
import { cleanObject, isViaWebView } from "../../../../helpers";
import useOrderflows from "../Orderflows/useOrderflows";

const useTransactionDetails = ({ orderId }) => {
  const { business_phone_code } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  const [isPrintModal, setIsPrintModal] = useState(false);
  const [isEditDate, setIsEditDate] = useState(false);
  const [isOrderCancel, setIsOrderCancel] = useState(false);
  const [isOrderCustomer, setIsOrderCustomer] = useState(false);
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();
  const [customerOrderDetails, setCustomerOrderDetails] = useState({
    customerPhoneCode: business_phone_code,
  });
  const [showRejectionReason, setShowRejectionReason] = useState(false);
  const [allowPrint, setAllowPrint] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isEditPaymentMethod, setIsEditPaymentMethod] = useState(false);
  const [allowPaymentReceipt, setAllowPaymentReceipt] = useState(false);
  const { useConfig, pingServer } = usePrint({ isEnabled: false });
  const [isEditTable, setIsEditTable] = useState(false);
  const [isUpdateNote, setIsUpdateNote] = useState(false);
  const [isInvoiceModal, setIsInvoiceModal] = useState(false);

  const { handlegenerateInvoice, isLoading: isGeneratingInvoice } =
    useGenerateInvoice();

  const { isLoading, orderDetails } = useOrderflows({
    orderId,
  });

  const printReceipt = async (billWasUpdated = false) => {
    if (
      isViaWebView() &&
      orderDetails?.store?.allow_direct_printing_on_android
    ) {
      triggerToast("Processing order reciept", "success");

      return window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          key: "salesReceipt",
          data: orderDetails,
        }),
      );
    }

    const isPingSuccessful = await pingServer();
    if (isPingSuccessful && useConfig) return setAllowPrint(true);

    setIsPrinting(true);
    printOrderReceipt(orderId, billWasUpdated)
      .then((response) => {
        const receiptLink =
          response?.data?.data?.url || response?.data?.data?.bill_url;
        window.open(receiptLink, "_blank");
        setIsPrinting(false);
      })
      .catch(() => {
        setIsPrinting(false);
      });
  };

  const printPaymentsReceipt = async () => {
    const isPingSuccessful = await pingServer();
    if (isPingSuccessful) return setAllowPrint(true);

    setAllowPaymentReceipt(true);
    printPaymentReceipt(orderId)
      .then((response) => {
        const receiptLink =
          response?.data?.data?.url || response?.data?.data?.bill_url;
        window.open(receiptLink, "_blank");
        setAllowPaymentReceipt(false);
      })
      .catch(() => {
        setAllowPaymentReceipt(false);
      });
  };

  const { mutate: printReturnsReceipt, isLoading: isPrintingReturns } =
    usePrintReturnsReceipt();
  const handlePrintReturnsReceipt = () => {
    printReturnsReceipt(
      { id: orderId },
      {
        onSuccess: (res) => {
          const receiptLink = res?.data?.data?.url;
          window.open(receiptLink, "_blank");
        },
      },
    );
  };

  const { cancelOrder, isLoading: isCancellingOrder } = useReverseOrder();
  const cancelCurrentOrder = () => {
    cancelOrder({
      data: cleanObject({ orderId: orderId }),
    });
  };

  const { isLoading: isProcessingOrder, mutate: mutateAcceptOrReject } =
    useAcceptOrRejectOrdereMutation();
  const acceptOrRejectOrder = ({ isAccept = true, rejectionReason = null }) => {
    mutateAcceptOrReject(
      {
        id: orderId,
        isAccept,
        rejection_reason: rejectionReason,
      },
      {
        onSuccess: (res) => {
          setShowRejectionReason(false);
          queryClient.invalidateQueries(["orderDetails", `${orderId}`]);
          queryClient.invalidateQueries(["getOrderList"]);
          triggerToast(res?.data?.detail, "success");
        },
      },
    );
  };

  return {
    isInvoiceModal,
    setIsInvoiceModal,
    isGeneratingInvoice,
    isCancellingOrder,
    isPrintModal,
    isEditDate,
    isOrderCustomer,
    customerOrderDetails,
    isProcessingOrder,
    showRejectionReason,
    isOrderCancel,
    allowPrint,
    isPrinting,
    isLoading,
    orderDetails,
    allowPaymentReceipt,
    isEditPaymentMethod,
    isPrintingReturns,
    isEditTable,
    isUpdateNote,
    setIsUpdateNote,
    setAllowPrint,
    setIsOrderCancel,
    acceptOrRejectOrder,
    setShowRejectionReason,
    setCustomerOrderDetails,
    setIsOrderCustomer,
    setIsEditDate,
    setIsPrintModal,
    cancelCurrentOrder,
    printReceipt,
    handlegenerateInvoice,
    printPaymentsReceipt,
    setIsEditPaymentMethod,
    handlePrintReturnsReceipt,
    setIsEditTable,
  };
};
export default useTransactionDetails;
